import { ChatSparkle24Regular, Shield24Regular, Checkmark24Regular, Dismiss24Regular, Question24Regular, Library24Regular } from "@fluentui/react-icons";
import styles from "./About.module.css";

export const About = () => {
    return (
        <main className={styles.pages}>
            <h1>About Ask Ethos</h1>
            <p className={styles.subtitle}>An AI-enabled chatbot providing federal ethics guidance and resources</p>

            {/* What Is Ask Ethos */}
            <section>
                <h2>
                    <ChatSparkle24Regular className={styles.sectionIcon} />
                    What Is Ask Ethos?
                </h2>
                <p>
                    Ask Ethos is an AI-enabled chatbot designed to help employees obtain quick, reliable guidance on federal ethics rules, filing requirements,
                    financial disclosures, conflicts of interest, gifts, outside activities, and general ethics policies.
                </p>
                <p>
                    Ask Ethos provides instant, searchable access to authoritative ethics materials, helping employees identify the correct forms, interpret
                    regulations, and understand required processes. While it does not replace official reviews by an Ethics Official, it makes it easier to
                    prepare accurate filings and locate relevant policies before seeking formal counsel.
                </p>
                <p>
                    <strong>Ask Ethos can answer questions such as:</strong>
                </p>
                <div className={styles.exampleQuestions}>
                    <div className={styles.exampleQuestion}>"Do I need to file an OGE-450 or OGE-278?"</div>
                    <div className={styles.exampleQuestion}>"Where do I submit my financial disclosure?"</div>
                    <div className={styles.exampleQuestion}>"What counts as a conflict of interest?"</div>
                    <div className={styles.exampleQuestion}>"Am I allowed to participate in this outside activity?"</div>
                    <div className={styles.exampleQuestion}>"What are the rules on gifts or travel reimbursements?"</div>
                    <div className={styles.exampleQuestion}>"Does the Hatch Act apply to my social media account?"</div>
                    <div className={styles.exampleQuestion}>"What is considered a widely attended gathering?"</div>
                    <div className={styles.exampleQuestion}>"Where can I find training for annual filing?"</div>
                </div>

                <p>
                    <strong>It can guide you through:</strong>
                </p>
                <ul>
                    <li>Determining which federal ethics forms you need</li>
                    <li>Understanding reportable assets, income, liabilities, and outside positions</li>
                    <li>Learning deadlines and requirements for annual or transaction reporting</li>
                    <li>Interpreting criminal conflict of interest statutes</li>
                    <li>Following gift rules, travel rules, and event participation policies</li>
                    <li>Understanding misconduct reporting, recusals, and impartiality rules</li>
                    <li>Finding legal advisory opinions related to your situation</li>
                    <li>Determining when to contact your agency's Ethics Official</li>
                </ul>
            </section>

            {/* How Ask Ethos Works */}
            <section>
                <h2>
                    <Library24Regular className={styles.sectionIcon} />
                    How Ask Ethos Works
                </h2>
                <p>
                    Ask Ethos draws upon a curated index of federal ethics regulations, statutes, guidance documents, training materials, and public financial
                    disclosure resources. It searches these materials and returns synthesized guidance with references to relevant authority.
                </p>
                <p>Ask Ethos uses the following steps:</p>
                <ul>
                    <li>
                        <strong>Searches a curated library</strong> — When you ask a question, Ask Ethos automatically looks through a secure collection of
                        government ethics regulations, policies, and training resources publicly available.
                    </li>
                    <li>
                        <strong>Retrieves relevant documents</strong> — Only trusted, authorized sources are used. Ask Ethos does not connect to the open
                        internet or pull information from unvetted sources.
                    </li>
                    <li>
                        <strong>Generates a clear explanation</strong> — Ask Ethos summarizes the retrieved documents and presents the information in plain
                        language, often providing direct links to forms or training materials.
                    </li>
                </ul>
            </section>

            {/* Security & Privacy */}
            <section>
                <h2>
                    <Shield24Regular className={styles.sectionIcon} />
                    Secure, Private, and Compliant
                </h2>
                <p>Ask Ethos was built with strict security and privacy protections in line with federal standards.</p>

                <p>
                    <strong>No PII or confidential data</strong> — Ask Ethos is not designed to receive or process:
                </p>
                <ul>
                    <li>Personal financial information (e.g., bank account numbers)</li>
                    <li>Official personnel data</li>
                    <li>Case-specific conflict of interest details</li>
                    <li>Sensitive or classified information</li>
                </ul>
                <p>The system will remind you not to enter PII or confidential information.</p>

                <p>
                    <strong>Restricted access</strong> — Ask Ethos can only reference pre-approved ethics resources. It cannot view internal filing systems,
                    employee inboxes, or protected personnel files.
                </p>

                <p>
                    <strong>Secure environment</strong> — Ask Ethos operates within an environment that meets federal security and compliance requirements. It
                    does not share information outside the organization.
                </p>
            </section>

            {/* What Ask Ethos Can and Cannot Do */}
            <section>
                <h2>What Ask Ethos Can—and Cannot—Do</h2>

                <div className={styles.twoColumn}>
                    <div className={styles.columnBox}>
                        <h3 className={`${styles.columnTitle} ${styles.canDoTitle}`}>
                            <Checkmark24Regular className={styles.canDoIcon} />
                            Ask Ethos CAN
                        </h3>
                        <ul>
                            <li>Help employees understand federal ethics rules</li>
                            <li>Point to relevant policies, regulations, and forms</li>
                            <li>Explain filing requirements and deadlines</li>
                            <li>Provide general guidance based on authoritative materials</li>
                            <li>Support ethics learning and awareness</li>
                        </ul>
                    </div>

                    <div className={styles.columnBox}>
                        <h3 className={`${styles.columnTitle} ${styles.cannotDoTitle}`}>
                            <Dismiss24Regular className={styles.cannotDoIcon} />
                            Ask Ethos CANNOT
                        </h3>
                        <ul>
                            <li>Make final legal determinations</li>
                            <li>Certify or formally review ethics filings</li>
                            <li>Access personal data or non-public financial disclosures</li>
                            <li>Give binding advice on specific individual cases</li>
                        </ul>
                    </div>
                </div>

                <p>For case-specific or sensitive questions, Ask Ethos will direct employees to contact their agency's Ethics Counselor.</p>
            </section>

            {/* FAQ */}
            <section>
                <h2>
                    <Question24Regular className={styles.sectionIcon} />
                    Frequently Asked Questions
                </h2>

                <div className={styles.faqItem}>
                    <h3 className={styles.faqQuestion}>Who can use Ask Ethos?</h3>
                    <p className={styles.faqAnswer}>Ask Ethos is available to anyone that has a question about federal ethics and compliance.</p>
                </div>

                <div className={styles.faqItem}>
                    <h3 className={styles.faqQuestion}>Does Ask Ethos replace ethics officials?</h3>
                    <p className={styles.faqAnswer}>
                        No. Ask Ethos supplements—but does not replace—the expertise of Ethics Officials. For complex or case-specific matters, Ask Ethos will
                        direct you to the appropriate contact.
                    </p>
                </div>

                <div className={styles.faqItem}>
                    <h3 className={styles.faqQuestion}>Is Ask Ethos secure?</h3>
                    <p className={styles.faqAnswer}>
                        Yes. Ask Ethos is implemented using approved technologies and follows federal privacy standards. Do not submit personal financial
                        details; the tool is designed for general guidance and policy navigation.
                    </p>
                </div>

                <div className={styles.faqItem}>
                    <h3 className={styles.faqQuestion}>Can Ask Ethos help me complete my annual filing?</h3>
                    <p className={styles.faqAnswer}>
                        Yes. Ask Ethos can walk you through which form you need, key deadlines, what to include, and how to access the proper filing systems.
                    </p>
                </div>
            </section>

            {/* Resources */}
            <section>
                <h2>
                    <Library24Regular className={styles.sectionIcon} />
                    Approved Ethics Resources
                </h2>
                <p>Ask Ethos generates responses from a variety of executive branch and agency-specific sources, including:</p>

                <div className={styles.resourceCategory}>
                    <h3 className={styles.resourceTitle}>Executive Branch Regulations</h3>
                    <p>
                        Core federal ethics laws including the Standards of Ethical Conduct (5 C.F.R. 2635), 18 U.S.C. §§ 201–209 (Criminal Conflict of Interest
                        Laws), and the 14 General Principles of Ethical Conduct.
                    </p>
                </div>

                <div className={styles.resourceCategory}>
                    <h3 className={styles.resourceTitle}>Financial Disclosure Resources</h3>
                    <p>
                        Guidance from the Office of Government Ethics (OGE) regarding OGE-450 and OGE-278 filings, including job aids, filing tips, and guidance
                        on Certificates of Divestiture.
                    </p>
                </div>

                <div className={styles.resourceCategory}>
                    <h3 className={styles.resourceTitle}>Political Activity & Hatch Act</h3>
                    <p>
                        Resources from the Office of Special Counsel (OSC) regarding prohibited and permitted political activities, social media restrictions,
                        and fundraising rules.
                    </p>
                </div>

                <div className={styles.resourceCategory}>
                    <h3 className={styles.resourceTitle}>Legal Advisories & Ethics Guidance</h3>
                    <p>
                        Topics include Widely Attended Gatherings (WAG), gift acceptance, outside employment, seeking employment, and post-employment
                        restrictions.
                    </p>
                </div>
            </section>
        </main>
    );
};

export default About;
